export const ROOT_ID = "root";
export const PATH_SEPARATOR = "/";
export const ROOM_PREFIX = "[";
export const ROOM_POSTFIX = "]";
export const CONTROLLER_ID = "controller";
const MODEL_ID_VALIDATE_REGEXP = /'[^a-zA-Z0-9_]'/;
/// Temporary variables to reduce GC ///
let ROOT_PATH;
/**
 * Utility class that handles model relationships encoded in paths. It can extract the room, parent and/or children
 * of a model path.
 */
export class ModelPath {
    constructor(path) {
        this.path = path;
        this.baseName = this.pathEnding(path);
    }
    set(path) {
        this.path = path;
        this.baseName = this.pathEnding(path);
        return this;
    }
    /**
     * Returns true if the ModelPath equals the string `root`
     */
    isRoot() {
        return this.path === ROOT_ID;
    }
    /**
     * Returns true if the last part of the ModelPath is a room i.e. `root/[rest]/rooms/[HQ]` would be a room while
     * `root/[rest]/rooms` wouldn't be a room.
     */
    isRoom() {
        return this.isRoot() || this.baseName.startsWith(ROOM_PREFIX) && this.baseName.endsWith(ROOM_POSTFIX);
    }
    /**
     * Returns a new ModelPath of the part of this model. So the parent path of `root/[rest]/rooms/[HQ]/table/cue`
     * would be the path `root/[rest]/rooms/[HQ]/table`.
     */
    getParent() {
        if (this.isRoot())
            return undefined;
        const lastSeparatorIndex = this.path.lastIndexOf(PATH_SEPARATOR);
        if (lastSeparatorIndex === -1)
            return undefined;
        const parentPath = this.path.substring(0, lastSeparatorIndex);
        return new ModelPath(parentPath);
    }
    /**
     * Returns a new ModelPath of the room this model is contained in. So the room path of `root/[rest]/rooms/[HQ]/table/cue`
     * would be the path `root/[rest]/rooms/[HQ]`.
     *
     * ToDO: Right now if the model is a room, then the same path is returned. Is this the expected behaviour?
     */
    getRoom() {
        const roomEndIndex = this.path.lastIndexOf(ROOM_POSTFIX);
        if (roomEndIndex === -1) {
            if (this.path.startsWith("root")) {
                return new ModelPath("root");
            }
            return undefined;
        }
        const roomPath = this.path.substring(0, roomEndIndex + 1);
        return new ModelPath(roomPath);
    }
    /**
     * Last part of the path without any pre- and postfixes
     */
    basename() {
        if (this.isRoom() && !this.isRoot()) {
            return this.baseName.substring(1, this.baseName.length - 1);
        }
        else {
            return this.baseName;
        }
    }
    /**
     * Returns a new ModelPath that is a combination of `this.path/path`. If this.path already has a path separator then
     * no separator is added between both paths. For this to work, the second path has to be a relative path.
     * @param path
     */
    concatenate(path) {
        const ending = path instanceof ModelPath ? path.toString() : path;
        const separator = this.path.endsWith(PATH_SEPARATOR) ? "" : PATH_SEPARATOR;
        return new ModelPath(`${this.path}${separator}${ending}`);
    }
    /**
     * Returns a list of each parts of the path without any room pre- and postfixes. So the path `root/[rest]/avatar`
     * would be returned as `["root", "rest", "avatar"]`
     */
    getHierarchyNames() {
        return this.path
            .split("/")
            .map(path => new ModelPath(path).basename());
    }
    /**
     * Returns the absolute path as a string e.g. `root/[rest]/avatar`
     */
    toString() {
        return this.path;
    }
    /// Static utility methods ///
    /**
     * Returns the ModelPath to the root model i.e. `root`
     */
    static rootPath() {
        if (ROOT_PATH !== undefined)
            return ROOT_PATH;
        ROOT_PATH = new ModelPath(ROOT_ID);
        return ROOT_PATH;
    }
    /**
     * Tries to create a ModelPath based on the provided properties. If no ModelPath could be created then undefined is returned
     *
     * Examples:
     * ```JS
     * ModelPath.resolve({ fullPath: "root/[rest]/rooms/[HQ]" }) // ->  "root/[rest]/rooms/[HQ]"
     * ModelPath.resolve({ name: "HQ", isRoom: true, parentPath: "root/[rest]/rooms" }) // ->  "root/[rest]/rooms/[HQ]"
     * ```
     */
    static resolve(props) {
        if (props === undefined)
            return undefined;
        if (props.fullPath !== undefined)
            return (props.fullPath instanceof ModelPath) ? props.fullPath : new ModelPath(props.fullPath);
        if (props.name !== undefined && props.parentPath !== undefined) {
            const parentPath = (props.parentPath instanceof ModelPath) ? props.parentPath : new ModelPath(props.parentPath);
            const name = (props.isRoom === true) ? `[${props.name}]` : props.name;
            return parentPath.concatenate(name);
        }
        return undefined;
    }
    /**
     * Verifies if the provided path is a ModelPath. A ModelPath has to start with `root` and uses path separators `/`.
     * A room must be indicated by an opening `[` and closing `]` respectively. And each part of the path can only use
     * alphanumeric characters as well as `_`.
     * @param path
     */
    static isValid(path) {
        const pathEntries = ModelPath.splitPath(path);
        if (pathEntries.length === 0 || pathEntries[0] !== ROOT_ID) {
            return false;
        }
        for (const pathEntry of pathEntries) {
            if (!ModelPath.validatePathEntry(pathEntry))
                return false;
        }
        return true;
    }
    /// Private path helper method ///
    static splitPath(path, pathSeparator = PATH_SEPARATOR) {
        return path.split(pathSeparator);
    }
    pathEnding(path, pathSeparator = PATH_SEPARATOR) {
        const parts = ModelPath.splitPath(path, pathSeparator);
        return parts[parts.length - 1];
    }
    /// Validation helper method ///
    static validatePathEntry(pathEntry) {
        if (pathEntry.startsWith(ROOM_PREFIX) && pathEntry.endsWith(ROOM_POSTFIX)) {
            // path entry defines a room
            const layout = pathEntry.substring(ROOM_PREFIX.length, pathEntry.length - ROOM_PREFIX.length - ROOM_POSTFIX.length + 1);
            return !MODEL_ID_VALIDATE_REGEXP.test(layout);
        }
        else if (pathEntry.startsWith(ROOM_PREFIX) || pathEntry.endsWith(ROOM_POSTFIX)) {
            // path entry start/ends with ROOM_PREFIX/ROOM_POSTFIX but the
            // corresponding ROOM_POSTFIX/ROOM_PREFIX is missing
            return false;
        }
        return !MODEL_ID_VALIDATE_REGEXP.test(pathEntry);
    }
}
